import React, { useState } from "react";
import { Table, Typography, Card, message, Button, Input, Space } from "antd";
import { FileExcelOutlined, SearchOutlined } from "@ant-design/icons";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import api from "../api";

const { Title } = Typography;

const ReporteFinalEnvioPage = () => {
    const [observacionFiltro, setObservacionFiltro] = useState("");
    const [data, setData] = useState([]);
    const [allCalibres, setAllCalibres] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {};
            if (observacionFiltro.trim() !== "") {
                params.observaciones = observacionFiltro.trim();
            }
            const res = await api.get("/proceso/report/despachos", { params });
            const fetchedData = res.data || [];

            // Collect all unique calibres across all records
            const calibresSet = new Set();
            fetchedData.forEach(item => {
                if (item.calibres && typeof item.calibres === "object") {
                    Object.keys(item.calibres).forEach(c => calibresSet.add(c));
                }
            });

            const uniqueCalibres = Array.from(calibresSet).sort((a, b) =>
                a.localeCompare(b, undefined, { numeric: true })
            );

            setAllCalibres(uniqueCalibres);
            setData(fetchedData);

            if (fetchedData.length === 0) {
                message.info("No se encontraron registros para ese código de envío.");
            } else {
                message.success(`Se encontraron ${fetchedData.length} registros.`);
            }
        } catch (err) {
            console.error(err);
            message.error("Error al cargar el reporte de despachos");
            setData([]);
            setAllCalibres([]);
        } finally {
            setLoading(false);
        }
    };

    const exportToExcel = async () => {
        if (!data || data.length === 0) {
            message.warning("No hay datos para exportar. Por favor genere el reporte primero.");
            return;
        }

        const workbook = new ExcelJS.Workbook();

        // Group data by exportadorNombre
        const groups = data.reduce((acc, item) => {
            const exp = item.exportadorNombre || "Sin Exportador";
            if (!acc[exp]) acc[exp] = [];
            acc[exp].push(item);
            return acc;
        }, {});

        const usedSheetNames = new Set();

        Object.keys(groups).forEach(exportador => {
            const items = groups[exportador];

            // Safe sheet name
            let baseName = exportador.replace(/[\/\\?*:\[\]]/g, "").trim();
            if (!baseName) baseName = "Hoja";
            let sheetName = baseName.substring(0, 31);
            let counter = 1;
            while (usedSheetNames.has(sheetName.toLowerCase())) {
                const suffix = `_${counter}`;
                sheetName = baseName.substring(0, 31 - suffix.length) + suffix;
                counter++;
            }
            usedSheetNames.add(sheetName.toLowerCase());

            const sheet = workbook.addWorksheet(sheetName);

            // Collect calibres for this group
            const calibresSet = new Set();
            items.forEach(item => {
                if (item.calibres && typeof item.calibres === "object") {
                    Object.keys(item.calibres).forEach(c => calibresSet.add(c));
                }
            });
            const calibres = Array.from(calibresSet).sort((a, b) =>
                a.localeCompare(b, undefined, { numeric: true })
            );

            // Headers — new order
            const headerRowData = [
                "Año",
                "Semana",
                "Exportador",
                "Fecha Pack",
                "Código de Envío",
                "Productor",
                "Producto",
                "Peso de Caja",
                "Secuencial",
                "Peso Ingresado (kg)",
                "Cajas Despachadas",
                ...calibres.map(c => `Calibre: ${c}`),
                "Rechazo (kg)",
                "% Rechazo"
            ];

            const headerRow = sheet.addRow(headerRowData);

            // Style header — green theme
            headerRow.eachCell(cell => {
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FF217346" }
                };
                cell.font = { color: { argb: "FFFFFFFF" }, bold: true, size: 11 };
                cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" }
                };
            });
            sheet.getRow(1).height = 30;

            // Data rows
            items.forEach((item, index) => {
                const pctRechazo = item.porcentajeRechazo != null
                    ? parseFloat(item.porcentajeRechazo).toFixed(2)
                    : (item.pesoIngresado > 0 ? ((item["rechazo en kg"] || 0) / item.pesoIngresado * 100).toFixed(2) : 0);

                const rowData = [
                    item.Year || "",
                    item["Week Number"] || "",
                    item.exportadorNombre || "Sin Exportador",
                    item.fecha || "",
                    item.packObservaciones || "",
                    item.productorName || "",
                    item.producto || "",
                    item["kg por caja"] || 0,
                    item.secuencial || "",
                    parseFloat((item.pesoIngresado || 0)),
                    item.cajasDespachadas || 0,
                    ...calibres.map(c =>
                        item.calibres && item.calibres[c] !== undefined ? item.calibres[c] : 0
                    ),
                    parseFloat((item["rechazo en kg"] || 0).toFixed(2)),
                    parseFloat((pctRechazo / 100).toFixed(4))
                ];

                const row = sheet.addRow(rowData);
                row.eachCell(cell => {
                    cell.border = {
                        top: { style: "thin" },
                        left: { style: "thin" },
                        bottom: { style: "thin" },
                        right: { style: "thin" }
                    };
                    cell.alignment = { vertical: "middle", horizontal: "center" };
                    if (index % 2 === 0) {
                        cell.fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "FFF9F9F9" }
                        };
                    }
                });
                // Format numeric columns to decimal number style
                row.getCell(10).numFmt = "#,##0.00";
                row.getCell(12 + calibres.length).numFmt = "#,##0.00";
                // Format "% Rechazo" column to percentage style
                row.getCell(13 + calibres.length).numFmt = "0.00%";
            });

            // Totals row
            const totalCajasDespachadas = items.reduce((s, r) => s + (r.cajasDespachadas || 0), 0);
            const totalPesoIngresado = items.reduce((s, r) => s + (r.pesoIngresado || 0), 0);
            const totalRechazo = items.reduce((s, r) => s + (r["rechazo en kg"] || 0), 0);
            const totalCalibresCols = calibres.map(c =>
                items.reduce((s, r) => s + (r.calibres && r.calibres[c] ? r.calibres[c] : 0), 0)
            );
            const totalPctRechazo = totalPesoIngresado > 0
                ? parseFloat((totalRechazo / totalPesoIngresado * 100).toFixed(2))
                : 0;

            const totalRowData = [
                "TOTAL GENERAL", "", "", "", "", "", "", "", "",
                parseFloat(totalPesoIngresado.toFixed(2)),
                totalCajasDespachadas,
                ...totalCalibresCols,
                parseFloat(totalRechazo.toFixed(2)),
                parseFloat((totalPctRechazo / 100).toFixed(4))
            ];

            const totalRow = sheet.addRow(totalRowData);
            totalRow.eachCell(cell => {
                cell.font = { bold: true };
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFE9F5EE" }
                };
                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "double" },
                    right: { style: "thin" }
                };
                cell.alignment = { vertical: "middle", horizontal: "center" };
            });
            // Format totals numeric columns to decimal number style
            totalRow.getCell(10).numFmt = "#,##0.00";
            totalRow.getCell(12 + calibres.length).numFmt = "#,##0.00";
            // Format totals "% Rechazo" column to percentage style
            totalRow.getCell(13 + calibres.length).numFmt = "0.00%";

            // Column widths
            const colWidths = [8, 10, 22, 20, 28, 22, 18, 16, 12, 18, 16];
            const colWidthsAfter = [14, 12]; // Rechazo, % Rechazo
            colWidths.forEach((w, i) => { sheet.getColumn(i + 1).width = w; });
            calibres.forEach((_, i) => { sheet.getColumn(colWidths.length + 1 + i).width = 14; });
            colWidthsAfter.forEach((w, i) => { sheet.getColumn(colWidths.length + calibres.length + 1 + i).width = w; });

        });

        // Generate and download
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
        });
        const dateStr = new Date().toISOString().split("T")[0];
        const obs = observacionFiltro.trim()
            ? `_${observacionFiltro.trim().replace(/\s+/g, "_").substring(0, 20)}`
            : "";
        saveAs(blob, `Reporte_Final_Envio${obs}_${dateStr}.xlsx`);
        message.success("Excel exportado exitosamente.");
    };

    // Base table columns — new order
    const baseColumns = [
        { title: "Año", dataIndex: "Year", key: "Year", width: 70 },
        { title: "Semana", dataIndex: "Week Number", key: "Week Number", width: 90 },
        { title: "Exportador", dataIndex: "exportadorNombre", key: "exportadorNombre", width: 140 },
        { title: "Fecha inicio", dataIndex: "fecha", key: "fecha", width: 160 },
        { title: "Código de Envío", dataIndex: "packObservaciones", key: "packObservaciones", width: 180 },
        { title: "Productor", dataIndex: "productorName", key: "productorName", width: 150 },
        { title: "Producto", dataIndex: "producto", key: "producto", width: 120 },
        { title: "Peso de Caja", dataIndex: "kg por caja", key: "kg por caja", width: 115 },
        { title: "Secuencial", dataIndex: "secuencial", key: "secuencial", width: 110 },
        {
            title: "Peso Ingresado (kg)",
            dataIndex: "pesoIngresado",
            key: "pesoIngresado",
            width: 155,
            render: (val) => val !== undefined && val !== null ? parseFloat(val).toFixed(2) : "-"
        },
        { title: "Cajas Desp.", dataIndex: "cajasDespachadas", key: "cajasDespachadas", width: 110 },
    ];

    const calibreColumns = allCalibres.map(c => ({
        title: `Calibre: ${c}`,
        key: `calibre_${c}`,
        width: 110,
        render: (_, record) =>
            record.calibres && record.calibres[c] !== undefined ? record.calibres[c] : "-"
    }));

    const afterCalibresColumns = [
        {
            title: "Rechazo (kg)",
            dataIndex: "rechazo en kg",
            key: "rechazo en kg",
            width: 115,
            render: (val) => val !== undefined && val !== null ? parseFloat(val).toFixed(2) : "-"
        },
        {
            title: "% Rechazo",
            dataIndex: "porcentajeRechazo",
            key: "porcentajeRechazo",
            width: 110,
            render: (val, record) => {
                const pct = val != null ? val : (record.pesoIngresado > 0 ? ((record["rechazo en kg"] || 0) / record.pesoIngresado * 100) : 0);
                return `${parseFloat(pct).toFixed(2)}%`;
            }
        }
    ];

    const columns = [...baseColumns, ...calibreColumns, ...afterCalibresColumns];

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: "16px" }}>
                <Title level={3} style={{ margin: 0 }}>Reporte Final de Envío</Title>
            </div>

            <Card style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", gap: "16px", alignItems: "flex-end", flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: "200px", maxWidth: "400px" }}>
                        <label style={{ display: "block", marginBottom: "4px", fontWeight: "bold" }}>
                            Filtrar por Código de Envío:
                        </label>
                        <Input
                            value={observacionFiltro}
                            onChange={e => setObservacionFiltro(e.target.value)}
                            onPressEnter={fetchData}
                            allowClear
                        />
                    </div>
                    <Space wrap>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={fetchData}
                            loading={loading}
                        >
                            Generar Reporte
                        </Button>
                        <Button
                            type="primary"
                            icon={<FileExcelOutlined />}
                            onClick={exportToExcel}
                            disabled={data.length === 0}
                            style={{ backgroundColor: "#217346", borderColor: "#217346" }}
                        >
                            Exportar a Excel
                        </Button>
                    </Space>
                </div>
            </Card>

            <Card>
                <Table
                    dataSource={data}
                    columns={columns}
                    rowKey={(record) => record.idpacks}
                    loading={loading}
                    pagination={{ pageSize: 20 }}
                    scroll={{ x: "max-content" }}
                    locale={{ emptyText: "Ingrese un código de envío y presione «Generar Reporte»" }}
                />
            </Card>
        </div>
    );
};

export default ReporteFinalEnvioPage;
